import React from "react";
import { Msr } from "./components";
import { money, summarizeQuote } from "./quoteRows";
import FamilyStatement, { hasStatementContent } from "./FamilyStatement";
import { parseMoneyCents, paymentAmountError, dueLabel } from "./instalments";
import "./t6.css";

// T6/T9 — Checkout review: the cart grouped per dancer + the server-computed
// payment summary. The breakdown is DATA-DRIVEN — it renders exactly the
// lines the quote returns (family fee / assessment may be absent; the late
// line appears only in the late window). Amounts are integer cents; the
// service fee is amount-only (the rate is never exposed to the client).
// When the school already has PAID entries for this event (roster dancers
// carry `entry`), the page switches to the add-entries delta state
// (9-add-entries.html): what you're adding + locked already-paid card +
// "Amount due now" summary. The engine's idempotency guarantees the quote
// only contains the new charges.

// Collapsible review section, per the design mockup (6-checkout.html).
function Section({ icon, name, meta, editLabel, onEdit, children }) {
  const [open, setOpen] = React.useState(true);
  return (
    <div className="sec">
      <div className="sec-h" onClick={() => setOpen((o) => !o)}>
        <button type="button" className="chev" aria-label={open ? "Collapse" : "Expand"}>
          <Msr style={{ transform: open ? "none" : "rotate(-90deg)" }}>expand_more</Msr>
        </button>
        <span className="ic"><Msr>{icon}</Msr></span>
        <div className="tt">
          <div className="nm">{name}</div>
          <div className="meta">{meta}</div>
        </div>
        {onEdit && (
          <button type="button" className="edit" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
            <Msr>edit</Msr>{editLabel || "Edit"}
          </button>
        )}
      </div>
      {open && <div className="sec-b">{children}</div>}
    </div>
  );
}

// "Pay in instalments" (card 15862648): offered only when the quote says the
// school is enabled (quote.instalments). The first payment confirms the whole
// entry; the balance is paid later from Current entries.
function PayChoice({ offer, totalCents, choice, onChange }) {
  if (!offer || !totalCents) return null;
  if (!offer.available) {
    return (
      <div className="paychoice">
        <div className="pc-note"><Msr>info</Msr>Your school has a balance owing on this event, so these additions are paid in full.</div>
      </div>
    );
  }
  const mode = choice.mode;
  const cents = parseMoneyCents(choice.amountText);
  const error = mode === "instalments" ? paymentAmountError(cents, totalCents, offer.minPaymentCents) : null;
  const errId = "oir-first-payment-err";
  const noteId = "oir-first-payment-note";
  return (
    <fieldset className="paychoice">
      <legend className="pc-h">How would you like to pay?</legend>
      <label className={"pc-opt" + (mode === "full" ? " on" : "")}>
        <input type="radio" name="oir-paymode" checked={mode === "full"} onChange={() => onChange({ ...choice, mode: "full" })} />
        <span className="pc-name">Pay in full</span>
        <span className="pc-amt">{money(totalCents)}</span>
      </label>
      <label className={"pc-opt" + (mode === "instalments" ? " on" : "")}>
        <input type="radio" name="oir-paymode" checked={mode === "instalments"} onChange={() => onChange({ ...choice, mode: "instalments" })} />
        <span className="pc-name">Pay in instalments</span>
      </label>
      {mode === "instalments" && (
        <div className="pc-inst">
          <label className="pc-lbl" htmlFor="oir-first-payment">Pay now</label>
          <div className="pc-input">
            <span>$</span>
            <input id="oir-first-payment" inputMode="decimal" placeholder="0.00" value={choice.amountText}
              aria-invalid={!!(error && choice.amountText)}
              aria-describedby={error && choice.amountText ? errId : noteId}
              onChange={(e) => onChange({ ...choice, amountText: e.target.value })} />
          </div>
          {error && choice.amountText
            ? <div className="pc-err" id={errId} role="alert">{error}</div>
            : (
              <div className="pc-note" id={noteId}>
                {cents && cents >= totalCents
                  ? <>That pays the whole entry now — no balance will remain.</>
                  : (
                    <>
                      {cents
                        ? <>Balance of {money(totalCents - cents)} due by {dueLabel(offer.balanceDueAt)}. </>
                        : <>The balance is due by {dueLabel(offer.balanceDueAt)}. </>}
                      Your entries are confirmed with this first payment; pay the rest any time from Current entries.
                    </>
                  )}
              </div>
            )}
        </div>
      )}
    </fieldset>
  );
}

export default function CheckoutReview({ event, roster, quote, quoting, onEditEntries, onEditReclaim, payChoice, onPayChoice }) {
  const breakdown = quote && quote.breakdown;
  const guard = (quote && quote.guard) || { ok: true, errors: [] };
  const teamGuard = (quote && quote.teamGuard) || { ok: true, errors: [], blocking: [] };
  const dancersById = React.useMemo(
    () => Object.fromEntries(roster.map((d) => [String(d._id), d])),
    [roster]
  );

  const items = (quote && quote.items) || [];
  const teams = (quote && quote.teams) || [];
  const entryCount = items.reduce((n, it) => n + (it.competitions || []).length, 0);
  const currency = ((breakdown && breakdown.currency) || (event && event.currency) || "").toUpperCase();
  // ENTRIES_CLOSED is a lock state, not something the teacher can fix — the
  // shell's lock card presents it, so it stays out of the fix-list here.
  const guardErrors = [...guard.errors, ...(teamGuard.blocking || [])].filter(
    (e) => e.code !== "ENTRIES_CLOSED"
  );
  const guardsOk = (guard.ok && !(teamGuard.blocking || []).length) || !guardErrors.length;

  // T9 — the school already has paid entries: this checkout is a DELTA.
  const paidDancers = roster.filter((d) => d.entry && (d.entry.competitions || []).length);
  const addMode = paidDancers.length > 0;

  return (
    <>
      {addMode && (
        <div className="addbanner">
          <span className="ic"><Msr>add_circle</Msr></span>
          <div>
            <div className="bt">Adding to your existing registration</div>
            <div className="bs">Only new entries are charged — nothing you've already paid for is billed again.</div>
          </div>
        </div>
      )}

      <div className="grid">
        <div>
          {!guardsOk && (
            <div className="guard-errors">
              <strong>Fix these before paying:</strong>
              <ul>
                {guardErrors.map((e, i) => {
                  const d = e.dancer_id && dancersById[e.dancer_id];
                  return <li key={i}>{d ? `${d.fname} ${d.lname}: ` : ""}{e.message}</li>;
                })}
              </ul>
            </div>
          )}

          {/* ONE list, not two (Devin 2026-08-19, both modes): the family
              statement IS the entries list — same grouping as the receipt the
              school gets, dancer lines carry the competition names and
              amounts. The aside keeps only the compact by-type totals. Team
              detail (dance, figure, override) lives on the Teams step. */}
          <div className="sec">
            <div className="addsec-h">
              <span className="ic"><Msr>{addMode ? "playlist_add" : "person"}</Msr></span>
              <span className="nm">{addMode ? "What you're adding" : "What you're registering"}</span>
              <span className="ct">
                {entryCount}{addMode ? " new" : ""} entr{entryCount === 1 ? "y" : "ies"}
                {teams.length > 0 && ` · ${teams.length}${addMode ? " new" : ""} team${teams.length === 1 ? "" : "s"}`}
                {quote && quote.statement && quote.statement.families.length > 1 &&
                  ` · ${quote.statement.families.length} families`}
              </span>
              {onEditEntries && (
                <button type="button" className="edit" onClick={onEditEntries}><Msr>edit</Msr>Edit</button>
              )}
            </div>
            {hasStatementContent(quote && quote.statement) ? (
              <FamilyStatement statement={quote.statement} currency={currency} pills={false} />
            ) : (
              <div className="add-row"><div className="who"><div className="det">{quoting ? "Calculating…" : "Nothing new yet."}</div></div></div>
            )}
          </div>

          {breakdown && breakdown.reclaim && breakdown.reclaim.rows && breakdown.reclaim.rows.length > 0 && (
            <Section icon="savings" name="Assessment reclaim"
              meta={`${breakdown.reclaim.rows.length} item${breakdown.reclaim.rows.length === 1 ? "" : "s"} allocated`}
              onEdit={onEditReclaim} editLabel="Edit">
              {breakdown.reclaim.rows.map((r, i) => (
                <div className="reclaim-line" key={i}>
                  <span className="nm">{r.label}</span>
                  <span className="calc">{r.qty} × {money(r.unitCents)}</span>
                  <span className="amt">{money(r.amountCents)}</span>
                </div>
              ))}
              <div className="reclaim-note">
                {breakdown.reclaim.sponsorshipCents > 0 && (
                  <><Msr>volunteer_activism</Msr>{money(breakdown.reclaim.sponsorshipCents)} of the assessment pool becomes a sponsorship.</>
                )}
                {breakdown.reclaim.overageCents > 0 && (
                  <><Msr>info</Msr>{money(breakdown.reclaim.overageCents)} exceeds the pool and is added to your total.</>
                )}
                {breakdown.reclaim.sponsorshipCents === 0 && breakdown.reclaim.overageCents === 0 && (
                  <><Msr>check_circle</Msr>Assessment pool fully allocated.</>
                )}
              </div>
            </Section>
          )}

          {addMode && (
            <div className="locked">
              <span className="ic"><Msr>lock</Msr></span>
              <div className="lt">
                <div className="h">Already registered &amp; paid (not re-charged)</div>
                <div className="s">{paidDancers.length} dancer{paidDancers.length === 1 ? "" : "s"} · their family fees &amp; assessments.</div>
              </div>
              <span className="paidtag"><Msr>check_circle</Msr>Paid</span>
            </div>
          )}
        </div>

        <div className="aside">
          <div className="pay">
            <h2 className={addMode ? "flush" : undefined}>{addMode ? "Amount due now" : "Payment summary"}</h2>
            {addMode && <div className="h-sub">New additions only</div>}
            <div className="pay-body">
              {quoting && <div className="prow"><span className="k">Calculating…</span></div>}
              {!quoting && !breakdown && <div className="prow"><span className="k">Nothing new to pay for.</span></div>}
              {!quoting && breakdown && (
                <>
                  {/* Compact by-type totals only — the per-family detail is
                      the main list; repeating it here was the two-lists
                      problem this layout replaced. */}
                  {summarizeQuote(breakdown, { add: addMode }).map((l, i) => (
                    <div className="prow" key={i}>
                      <span className="k">{l.label}{l.sub && <span className="subk">{l.sub}</span>}</span>
                      <span className="v">{money(l.amount)}</span>
                    </div>
                  ))}
                  {breakdown.subtotalCents != null && (
                    <div className="prow subtotal"><span className="k">Subtotal</span><span className="v">{money(breakdown.subtotalCents)}</span></div>
                  )}
                  {breakdown.serviceCents != null && breakdown.serviceCents > 0 && (
                    <div className="prow"><span className="k">Service fee</span><span className="v">{money(breakdown.serviceCents)}</span></div>
                  )}
                  <div className="prow total">
                    <span className="k">{addMode ? "Total due now" : "Total"}</span>
                    <span className="v">{money(breakdown.totalCents)}</span>
                  </div>
                </>
              )}
            </div>
            {!quoting && breakdown && payChoice && onPayChoice && (
              <PayChoice offer={quote && quote.instalments} totalCents={breakdown.totalCents}
                choice={payChoice} onChange={onPayChoice} />
            )}
            <div className="paynote">
              <Msr>info</Msr>
              {addMode
                ? "No refunds. Add-only — existing entries can't be removed."
                : "No refunds. You can still add entries before the deadline."}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
